import { useEffect, useState } from 'react';
import { Headset, Bot, Activity, type LucideIcon } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { FlagToggleCard } from './flag-toggle-card';
import { PulseImportHistory } from './pulse-import-history';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useAccountMutations } from '@/hooks/use-account';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { PRODUCT_LABELS } from '@/constants/feature-labels';
import type { FeatureGuards, AiInsights, Products } from '@/types';

interface Props {
  featureGuards: FeatureGuards;
  aiInsights: AiInsights;
  products: Products;
}

const AI_INSIGHT_KEYS = [
  'insight_generation',
  'ai_suggestion_generation',
  'ai_actions',
  'conversation_classification'
];

const ICONS: Record<string, LucideIcon> = {
  helpdesk: Headset,
  kim_ai: Bot,
  pulse: Activity
};

interface ProductCardProps {
  product: string;
  enabled: boolean;
  busy: boolean;
  onToggle: (next: boolean) => void;
  children?: React.ReactNode;
}

function ProductCard({
  product,
  enabled,
  busy,
  onToggle,
  children
}: ProductCardProps) {
  const meta = PRODUCT_LABELS[product];
  const Icon = ICONS[product];
  return (
    <Card
      className={cn(
        'gap-0 overflow-hidden p-0 transition-colors',
        enabled ? 'border-primary/30' : ''
      )}
    >
      <div
        className={cn(
          'flex items-center gap-4 p-5',
          enabled ? 'bg-primary/[0.03]' : ''
        )}
      >
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
            enabled
              ? 'bg-primary/10 text-primary'
              : 'bg-muted text-muted-foreground'
          )}
        >
          <Icon className='h-5 w-5' />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='font-medium'>{meta.label}</p>
          <p className='text-xs text-muted-foreground'>{meta.tooltip}</p>
        </div>
        <Switch
          checked={enabled}
          disabled={busy}
          onCheckedChange={onToggle}
        />
      </div>
      {enabled && children && (
        <div className='border-t bg-card p-5'>{children}</div>
      )}
    </Card>
  );
}

/** The account itself, as a value the brand <Select> can hold — it cannot hold null. */
const ACCOUNT_SCOPE = 'account';

export function ProductsFeaturesSection({
  featureGuards,
  aiInsights,
  products
}: Props) {
  const {
    getFeatureGuards,
    updateFeatureGuards,
    updateAiInsights,
    updateProduct
  } = useAccountMutations();
  const { data: businessUnits = [] } = useBusinessUnits();

  // WHOSE guards are on screen. The account's are what every brand without its own value
  // reads, so it stays the default view: opening this tab on a brand would hide the values
  // most of the account actually runs on.
  const [scope, setScope] = useState<string>(ACCOUNT_SCOPE);
  const buId = scope === ACCOUNT_SCOPE ? null : Number(scope);
  const [owned, setOwned] = useState<string[]>([]);
  const [loadingScope, setLoadingScope] = useState(false);

  const [guards, setGuards] = useState<FeatureGuards>(featureGuards);
  const [insights, setInsights] = useState<AiInsights>(aiInsights);
  const [prod, setProd] = useState<Products>(products);
  const [busy, setBusy] = useState<string | null>(null);

  const insightsOn = AI_INSIGHT_KEYS.every((k) => Boolean(insights[k]));

  // Re-read on every scope change. The account's guards arrive with the account detail, but a
  // brand's are only knowable from the scoped endpoint — and so is which of them it owns.
  useEffect(() => {
    let cancelled = false;
    setLoadingScope(true);
    getFeatureGuards(buId)
      .then((res) => {
        if (cancelled) return;
        setGuards(res.featureGuards);
        setOwned(res.ownedFeatureGuards ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : 'Could not load flags');
      })
      .finally(() => {
        if (!cancelled) setLoadingScope(false);
      });
    return () => {
      cancelled = true;
    };
    // getFeatureGuards closes over the selected account id, which is stable for this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buId]);

  const patchGuards = async (patch: Partial<FeatureGuards>) => {
    setBusy('kim_ai_guard');
    const prev = guards;
    const prevOwned = owned;
    setGuards({ ...guards, ...patch } as FeatureGuards);
    try {
      const res = await updateFeatureGuards(patch, buId);
      setGuards(res.featureGuards);
      setOwned(res.ownedFeatureGuards ?? []);
      toast.success(buId == null ? 'Updated' : `Updated for ${scopeName}`);
    } catch (err) {
      setGuards(prev);
      setOwned(prevOwned);
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  const toggleInsights = async (next: boolean) => {
    setBusy('helpdesk_guard');
    const prev = insights;
    const patch = Object.fromEntries(AI_INSIGHT_KEYS.map((k) => [k, next]));
    setInsights({ ...insights, ...patch });
    try {
      const res = await updateAiInsights(patch);
      setInsights(res.aiInsights);
      toast.success('Updated');
    } catch (err) {
      setInsights(prev);
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  const toggleProductEnabled = async (product: string, next: boolean) => {
    setBusy(product);
    const prev = prod;
    setProd({ ...prod, [product]: next });
    try {
      const res = await updateProduct(product, { enabled: next });
      setProd(res.products);
      toast.success(
        `${PRODUCT_LABELS[product].label} ${next ? 'enabled' : 'disabled'}`
      );
    } catch (err) {
      setProd(prev);
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  const aiOn = Boolean(guards.v3 || guards.v3_shadow);
  const scopeName =
    buId == null
      ? 'the account'
      : (businessUnits.find((b) => b.id === buId)?.name ?? `brand ${buId}`);
  const isInherited = (key: string) => buId != null && !owned.includes(key);
  const guardsBusy = busy === 'kim_ai_guard' || loadingScope;

  return (
    <div className='space-y-4'>
      {/* Kim AI — product + kim-ai feature guards */}
      <ProductCard
        product='kim_ai'
        enabled={Boolean(prod.kim_ai)}
        busy={busy === 'kim_ai'}
        onToggle={(next) => toggleProductEnabled('kim_ai', next)}
      >
        <div className='space-y-3'>
          {/* WHOSE flags these are. The account's row is what every brand without its own
              value reads, so it leads the list and is the default. Picking a brand shows what
              that brand actually runs on, inherited values included. */}
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-xs font-medium text-muted-foreground'>
              These flags apply to
            </span>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger size='sm' className='w-56'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ACCOUNT_SCOPE}>
                  The whole account
                </SelectItem>
                {businessUnits.map((unit) => (
                  <SelectItem key={unit.id} value={String(unit.id)}>
                    {unit.name}
                    {unit.is_default ? ' (default)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className='text-xs text-muted-foreground'>
            {buId == null
              ? 'Every brand without a value of its own reads these, and moves whenever they change.'
              : `${scopeName} reads its own value where it has one and the account's everywhere else. Toggling a switch here makes it this brand's own.`}
          </p>

          <div className='grid gap-3 md:grid-cols-2'>
            <FlagToggleCard
              label='AI'
              tooltip='Turns Kim AI on for this scope. When on, the AI reads and responds to customer conversations.'
              checked={aiOn}
              disabled={guardsBusy}
              inherited={isInherited('v3') && isInherited('v3_shadow')}
              onToggle={(next) =>
                patchGuards(next ? { v3: true } : { v3: false, v3_shadow: false })
              }
            />
            <FlagToggleCard
              label='Shadow mode'
              tooltip='When on, the AI only drafts replies for your team to review and never sends them to customers.'
              checked={Boolean(guards.shadow_mode_enabled)}
              disabled={guardsBusy || !aiOn}
              disabledHint={!aiOn ? 'Turn on AI first.' : undefined}
              inherited={isInherited('shadow_mode_enabled')}
              onToggle={(next) => patchGuards({ shadow_mode_enabled: next })}
            />
            <FlagToggleCard
              label='AI settings access'
              tooltip='Lets this scope see and edit its own AI settings.'
              checked={Boolean(guards.ai_settings_enabled)}
              disabled={guardsBusy}
              inherited={isInherited('ai_settings_enabled')}
              onToggle={(next) => patchGuards({ ai_settings_enabled: next })}
            />
            <FlagToggleCard
              label='Social media agent'
              tooltip='Lets the agent reply to and moderate Facebook and Instagram comments, using the rules set up for this scope. Off means it never acts.'
              checked={Boolean(guards.social_agent_enabled)}
              disabled={guardsBusy}
              inherited={isInherited('social_agent_enabled')}
              onToggle={(next) => patchGuards({ social_agent_enabled: next })}
            />
            <FlagToggleCard
              label='Emergency stop'
              tooltip='Immediately pauses all automatic AI replies for this scope.'
              checked={Boolean(guards.intent_automation_kill_switch)}
              disabled={guardsBusy}
              inherited={isInherited('intent_automation_kill_switch')}
              onToggle={(next) =>
                patchGuards({ intent_automation_kill_switch: next })
              }
            />
          </div>
        </div>
      </ProductCard>

      {/* Helpdesk — candy AI Insights */}
      <ProductCard
        product='helpdesk'
        enabled={Boolean(prod.helpdesk)}
        busy={busy === 'helpdesk'}
        onToggle={(next) => toggleProductEnabled('helpdesk', next)}
      >
        <FlagToggleCard
          label='AI Insights'
          tooltip='Turns on all insight features together: insight summaries, reply suggestions, AI actions, and automatic conversation categorization.'
          checked={insightsOn}
          disabled={busy === 'helpdesk_guard'}
          onToggle={toggleInsights}
        />
      </ProductCard>

      {/* Pulse — the ticket import */}
      <ProductCard
        product='pulse'
        enabled={Boolean(prod.pulse)}
        busy={busy === 'pulse'}
        onToggle={(next) => toggleProductEnabled('pulse', next)}
      >
        <div className='max-w-lg'>
          <PulseImportHistory />
        </div>
      </ProductCard>
    </div>
  );
}
